// GST invoice layout using @react-pdf/renderer
// Receives a single canonical InvoiceDisplayModel — no tax math inside this component.
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { fmtInr, numberToWords } from "./invoiceUtils";

const GstLayout = ({ model }) => {
  const {
    invoice_number,
    invoice_date,
    due_date,
    status,
    notes,
    terms,
    copyType,
    items,
    subtotal,
    discountPercent,
    discountAmount,
    cgstPercent,
    cgstAmount,
    sgstPercent,
    sgstAmount,
    igstPercent,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    customerShipped,
    template,
    transport,
    e_way_bill_no,
    irn_number,
    reverse_charge,
  } = model || {};

  const t = template || {};
  const primary = t.primary_color || "#002FA7";
  
  // Get item columns from template, default to GST standard columns
  const itemColumns = Array.isArray(t.item_columns) 
    ? t.item_columns 
    : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];

  // Use shipped customer if provided, otherwise bill-to customer
  const shipToCustomer = customerShipped || customer;

  const styles = StyleSheet.create({
    page: {
      padding: 25,
      fontFamily: "Helvetica",
      fontSize: 8,
    },
    gstHeader: {
      flexDirection: "row",
      marginBottom: 12,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: "#ddd",
    },
    gstin: {
      fontSize: 10,
      color: "#333",
      width: "25%",
    },
    invoiceTitleCenter: {
      width: "50%",
      textAlign: "center",
    },
    copyType: {
      fontSize: 10,
      width: "25%",
      textAlign: "right",
    },
    invoiceTitle: {
      fontSize: 12,
      fontWeight: "bold",
      color: primary,
      letterSpacing: 1,
      textTransform: "uppercase",
    },
    cancelled: {
      fontSize: 16,
      fontWeight: "bold",
      color: "#dc2626",
      marginTop: 4,
    },
    companyName: {
      textAlign: "center",
      marginBottom: 8,
    },
    companyNameText: {
      fontSize: 20,
      fontWeight: "bold",
      color: "#000",
    },
    companyDescription: {
      fontSize: 9,
      color: "#666",
      marginTop: 3,
      fontStyle: "italic",
    },
    orgInfo: {
      fontSize: 8,
      color: "#555",
      textAlign: "center",
      marginBottom: 8,
      lineHeight: 1.3,
    },
    detailsGrid: {
      flexDirection: "column",
      marginBottom: 12,
    },
    detailsRow: {
      flexDirection: "row",
      marginBottom: 8,
    },
    detailsBox: {
      flex: 1,
      backgroundColor: "#f9f9f9",
      padding: 8,
      marginHorizontal: 4,
    },
    invoiceBox: {
      flex: 1,
      backgroundColor: "#f9f9f9",
      padding: 8,
      marginHorizontal: 4,
    },
    billingBox: {
      flex: 1,
      backgroundColor: "#f9f9f9",
      padding: 8,
      marginHorizontal: 4,
    },
    transportBox: {
      flex: 1,
      backgroundColor: "#f9f9f9",
      padding: 8,
      marginHorizontal: 4,
    },
    shippingBox: {
      flex: 1,
      backgroundColor: "#f9f9f9",
      padding: 8,
      marginHorizontal: 4,
    },
    detailsBoxTitle: {
      marginBottom: 6,
      fontWeight: "bold",
      color: primary,
      fontSize: 8,
      letterSpacing: 1,
      textTransform: "uppercase",
    },
    fieldRow: {
      flexDirection: "row",
      marginBottom: 3,
    },
    detailsLabel: {
      width: 70,
      color: "#666",
      fontSize: 8,
    },
    detailsValue: {
      flex: 1,
      fontSize: 8,
    },
    detailsValueBold: {
      fontWeight: "bold",
    },
    table: {
      width: "100%",
      marginBottom: 12,
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#e5e5e5",
      color: "#000",
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#ddd",
    },
    tableCell: {
      padding: 6,
      fontSize: 8,
    },
    textCenter: {
      textAlign: "center",
    },
    textRight: {
      textAlign: "right",
    },
    summaryGrid: {
      flexDirection: "row",
      marginBottom: 20,
    },
    summaryBox: {
      flex: 1,
      backgroundColor: "#f9f9f9",
      padding: 10,
      marginHorizontal: 4,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 3,
    },
    summaryTotal: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: 8,
      paddingTop: 8,
      borderTopWidth: 1,
      borderTopColor: "#ddd",
      fontWeight: "bold",
    },
    bankDetails: {
      padding: 8,
      marginBottom: 8,
    },
    termsNotes: {
      padding: 8,
    },
    bankTermsRow: {
      flexDirection: "column",
      marginBottom: 15,
    },
    termsTitle: {
      marginBottom: 5,
      fontWeight: "bold",
      color: primary,
      fontSize: 8,
      letterSpacing: 1,
      textTransform: "uppercase",
    },
    signature: {
      textAlign: "right",
      paddingTop: 40,
    },
    signatureLine: {
      borderTopWidth: 1,
      borderTopColor: "#000",
      paddingTop: 4,
      minWidth: 100,
      display: "inline-block",
    },
    footer: {
      paddingTop: 6,
      marginTop: 8,
      textAlign: "center",
      fontSize: 7,
      color: "#999",
    },
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* GST Header */}
        <View style={styles.gstHeader}>
          <Text style={styles.gstin}>
            <Text style={{ color: "#666" }}>GSTIN:</Text>{" "}
            {organization?.gst_number || "-"}
          </Text>
          <View style={styles.invoiceTitleCenter}>
            <Text style={styles.invoiceTitle}>
              {t.invoice_title || "Tax Invoice"}
            </Text>
            {status === "cancelled" && (
              <Text style={styles.cancelled}>CANCELLED</Text>
            )}
          </View>
          <Text style={styles.copyType}>
            {copyType === "original"
              ? "Original Copy"
              : copyType === "duplicate"
              ? "Duplicate Copy"
              : "Triplicate Copy"}
          </Text>
        </View>

        {/* Centered Company Name */}
        <View style={styles.companyName}>
          <Text style={styles.companyNameText}>
            {organization?.organization_name || "Your Business"}
          </Text>
          {organization?.description && (
            <Text style={styles.companyDescription}>
              {organization.description}
            </Text>
          )}
        </View>

        {/* Organization Info */}
        <View style={styles.orgInfo}>
          {organization?.address && <Text>{organization.address}</Text>}
          <Text>
            {[organization?.city, organization?.state, organization?.pincode]
              .filter(Boolean)
              .join(", ")}
            {organization?.state_code && (
              <> · State Code: {organization.state_code}</>
            )}
          </Text>
          <Text>
            {organization?.email && <>Email: {organization.email}</>}
            {organization?.email && organization?.phone && <> · </>}
            {organization?.phone && <>Tel: {organization.phone}</>}
          </Text>
          {organization?.website && (
            <Text>Website: {organization.website}</Text>
          )}
        </View>

        {/* Invoice Details Grid - 2 Row Layout */}
        <View style={styles.detailsGrid}>
          {/* Row 1: Invoice Details (1/3) + More Details (2/3) */}
          <View style={styles.detailsRow}>
            <View style={styles.invoiceBox}>
              <Text style={styles.detailsBoxTitle}>Invoice Details</Text>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Invoice No:</Text>
                <Text
                  style={[styles.detailsValue, styles.detailsValueBold]}
                >
                  {invoice_number}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Invoice Date:</Text>
                <Text style={styles.detailsValue}>{invoice_date}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Due Date:</Text>
                <Text style={styles.detailsValue}>{due_date || "-"}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Reverse Charge:</Text>
                <Text style={styles.detailsValue}>{reverse_charge ? "Yes" : "No"}</Text>
              </View>
            </View>
            <View style={styles.transportBox}>
              <Text style={styles.detailsBoxTitle}>More Details</Text>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Transport:</Text>
                <Text style={styles.detailsValue}>
                  {transport || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>E-Way Bill:</Text>
                <Text style={styles.detailsValue}>
                  {e_way_bill_no || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>IRN No:</Text>
                <Text style={styles.detailsValue}>
                  {irn_number || "-"}
                </Text>
              </View>
            </View>
          </View>

          {/* Row 2: Billing Details (1/3) + Shipping Details (2/3) */}
          <View style={styles.detailsRow}>
            <View style={styles.billingBox}>
              <Text style={styles.detailsBoxTitle}>Billing Details</Text>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Name:</Text>
                <Text
                  style={[styles.detailsValue, styles.detailsValueBold]}
                >
                  {customer?.name || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Address:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.address || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>City:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.city || "-"}
                </Text>
                <Text style={styles.detailsLabel}>State:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.state || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>State Code:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.state_code || "-"}
                </Text>
                <Text style={styles.detailsLabel}>Pincode:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.pincode || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Phone:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.phone || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>GSTIN:</Text>
                <Text style={styles.detailsValue}>
                  {customer?.gst || "-"}
                </Text>
              </View>
            </View>
            <View style={styles.shippingBox}>
              <Text style={styles.detailsBoxTitle}>Shipping Details</Text>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Name:</Text>
                <Text
                  style={[styles.detailsValue, styles.detailsValueBold]}
                >
                  {shipToCustomer?.name || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Address:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.address || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>City:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.city || "-"}
                </Text>
                <Text style={styles.detailsLabel}>State:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.state || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>State Code:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.state_code || "-"}
                </Text>
                <Text style={styles.detailsLabel}>Pincode:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.pincode || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Phone:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.phone || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>GSTIN:</Text>
                <Text style={styles.detailsValue}>
                  {shipToCustomer?.gst || "-"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text
              style={[styles.tableCell, styles.textCenter, { flex: 0.5 }]}
            >
              Sr No.
            </Text>
            {itemColumns.map((col) => {
              const columnConfig = {
                item_name: { label: "Item", flex: 3, align: "left" },
                description: { label: "Description", flex: 2, align: "left" },
                hsn_sac: { label: "HSN/SAC", flex: 1, align: "center" },
                quantity: { label: "Qty", flex: 0.5, align: "center" },
                unit: { label: "Unit", flex: 0.5, align: "center" },
                price: { label: "Rate", flex: 1, align: "right" },
                amount: { label: "Amount", flex: 1, align: "right" },
              };
              const config = columnConfig[col] || { label: col, flex: 1, align: "left" };
              return (
                <Text
                  key={col}
                  style={[
                    styles.tableCell,
                    config.align === "center" && styles.textCenter,
                    config.align === "right" && styles.textRight,
                    { flex: config.flex }
                  ]}
                >
                  {config.label}
                </Text>
              );
            })}
          </View>
          {(items || []).map((item, idx) => (
            <View key={item.id || idx} style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCell,
                  styles.textCenter,
                  { flex: 0.5 },
                ]}
              >
                {idx + 1}
              </Text>
              {itemColumns.map((col) => {
                const columnConfig = {
                  item_name: { flex: 3, align: "left" },
                  description: { flex: 2, align: "left" },
                  hsn_sac: { flex: 1, align: "center" },
                  quantity: { flex: 0.5, align: "center" },
                  unit: { flex: 0.5, align: "center" },
                  price: { flex: 1, align: "right" },
                  amount: { flex: 1, align: "right" },
                };
                const config = columnConfig[col] || { flex: 1, align: "left" };
                const value = col === "price" ? fmtInr(item.price) 
                  : col === "amount" ? fmtInr(item.amount)
                  : item[col] || "-";
                return (
                  <Text
                    key={col}
                    style={[
                      styles.tableCell,
                      config.align === "center" && styles.textCenter,
                      config.align === "right" && styles.textRight,
                      { flex: config.flex }
                    ]}
                  >
                    {value}
                  </Text>
                );
              })}
            </View>
          ))}
        </View>

        {/* Summary Section */}
        <View style={styles.summaryGrid} wrap={false}>
          <View style={styles.summaryBox}>
            <Text style={styles.detailsBoxTitle}>Amount in Words</Text>
            <Text style={{ fontStyle: "italic", lineHeight: 1.4 }}>
              {numberToWords(total || 0)}
            </Text>
          </View>
          <View style={styles.summaryBox}>
            <Text style={styles.detailsBoxTitle}>Tax Summary</Text>
            <View style={styles.summaryRow}>
              <Text>Subtotal:</Text>
              <Text>{fmtInr(subtotal)}</Text>
            </View>
            {discountAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text>Discount ({discountPercent}%):</Text>
                <Text>-{fmtInr(discountAmount)}</Text>
              </View>
            )}
            {cgstAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text>CGST ({cgstPercent}%):</Text>
                <Text>{fmtInr(cgstAmount)}</Text>
              </View>
            )}
            {sgstAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text>SGST ({sgstPercent}%):</Text>
                <Text>{fmtInr(sgstAmount)}</Text>
              </View>
            )}
            {igstAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text>IGST ({igstPercent}%):</Text>
                <Text>{fmtInr(igstAmount)}</Text>
              </View>
            )}
            {shipping > 0 && (
              <View style={styles.summaryRow}>
                <Text>Shipping:</Text>
                <Text>{fmtInr(shipping)}</Text>
              </View>
            )}
            {roundoff !== 0 && Math.abs(roundoff) > 0.01 && (
              <View style={styles.summaryRow}>
                <Text>Roundoff:</Text>
                <Text>
                  {roundoff > 0 ? "+" : ""}
                  {fmtInr(roundoff)}
                </Text>
              </View>
            )}
            <View style={styles.summaryTotal}>
              <Text>Total:</Text>
              <Text>{fmtInr(total)}</Text>
            </View>
          </View>
        </View>

        {/* Bank Details & Terms Row */}
        <View style={styles.bankTermsRow}>
          {t.show_bank_details !== false && (
            <View style={styles.bankDetails} wrap={false}>
              <Text style={styles.detailsBoxTitle}>Bank Details</Text>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Bank Name:</Text>
                <Text style={styles.detailsValue}>
                  {organization?.bank_name || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>Account No:</Text>
                <Text style={styles.detailsValue}>
                  {organization?.account_number || "-"}
                </Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.detailsLabel}>IFSC Code:</Text>
                <Text style={styles.detailsValue}>
                  {organization?.ifsc || "-"}
                </Text>
              </View>
            </View>
          )}

          {t.show_terms !== false && terms && (
            <View style={styles.termsNotes} wrap={false}>
              <Text style={styles.termsTitle}>Terms & Conditions</Text>
              <Text style={{ lineHeight: 0.5 }}>{terms}</Text>
            </View>
          )}
        </View>

        {/* Signature */}
        {t.show_signature !== false && (
          <View style={styles.signature} wrap={false}>
            {organization?.signature ? (
              <Image
                src={organization.signature}
                style={{ maxHeight: 50, maxWidth: 120, marginBottom: 4, objectFit: "contain" }}
              />
            ) : null}
            <View style={styles.signatureLine}>
              <Text style={{ fontSize: 9, fontWeight: "bold" }}>
                Authorized Signatory
              </Text>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer} wrap={false}>
          {t.footer_text && <Text>{t.footer_text}</Text>}
          {t.show_powered_by !== false && <Text>Powered by BillForge</Text>}
        </View>
      </Page>
    </Document>
  );
};

GstLayout.displayName = "GstLayout";

export default GstLayout;
